import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { ShieldCheck, Lock, Mail, Loader } from 'lucide-react';

// ¿El navegador va a guardar la sesión? (20/10/2026) La sesión de Supabase vive en localStorage.
// En Safari con Navegación privada se borra al cerrar la pestaña, y con "Bloquear todas las cookies"
// ni siquiera se guarda (queda en memoria hasta recargar) → el admin "se desloguea solo" y con el 2FA
// hay que pedir un código cada vez. Medido: un iPhone con Safari logueándose 24 veces en 30 días.
// Detección de privado en Safari: OPFS (navigator.storage.getDirectory) falla con
// "unknown transient reason" solo en ventanas privadas (mismo truco que la lib detectIncognito).
async function sessionWontPersist(): Promise<'blocked' | 'private' | null> {
    try {
        localStorage.setItem('__krilic_probe', '1');
        localStorage.removeItem('__krilic_probe');
    } catch { return 'blocked'; }
    try {
        const st: any = (navigator as any).storage;
        if (st?.getDirectory) await st.getDirectory();
    } catch (e: any) {
        if (String(e?.message || e).includes('unknown transient reason')) return 'private';
    }
    return null;
}

export function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [storageWarn, setStorageWarn] = useState<'blocked' | 'private' | null>(null);
    useEffect(() => { sessionWontPersist().then(setStorageWarn); }, []);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;
        } catch (err: any) {
            setError(err.message || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <div className="logo-circle">
                        <ShieldCheck size={40} className="text-secondary" />
                    </div>
                    <h1>Bienvenido a Krilic v2</h1>
                    <p>Ingresa tus credenciales para continuar</p>
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    {storageWarn && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            {storageWarn === 'private'
                                ? 'Estás en Navegación privada: la sesión se cierra cuando cerrás la pestaña y vas a tener que entrar (y pedir el código) cada vez. Abrí el admin en una pestaña normal.'
                                : 'Tu navegador está bloqueando el almacenamiento (en iPhone: Ajustes → Safari → desactivá "Bloquear todas las cookies"). Sin eso la sesión se cierra cada vez que recargás.'}
                        </div>
                    )}
                    {error && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Correo Electrónico</label>
                        <div className="input-wrapper">
                            <Mail size={18} className="input-icon" />
                            <input
                                id="email"
                                type="email"
                                placeholder="admin@krilic.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Contraseña</label>
                        <div className="input-wrapper">
                            <Lock size={18} className="input-icon" />
                            <input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <button type="submit" className="login-btn" disabled={loading}>
                        {loading ? <Loader size={18} className="animate-spin" /> : 'Iniciar Sesión'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>Panel de Administración Seguro</p>
                </div>
            </div>
        </div>
    );
}
